import React, { useEffect, useState } from 'react';
import { AsyncStorage, executeLogout } from 'services';
import { trackingLogout, trackingLogin } from 'services/analytics';
import { BootScreen, configureBootScreen } from 'screens';
import { removeGroupLoginGroupId } from 'helpers';

// Types
type Action =
  | { type: 'LOGIN'; payload: any }
  | { type: 'LOGOUT' }
  | { type: 'GROUP_LOGIN_SET_ID'; payload: any }
  | { type: 'GROUP_LOGIN_UNSET_ID' };
type Dispatch = (action: Action) => void;
type State = {
  isAuthenticated: boolean;
  accessToken: string;
  refreshToken: string;
  groupLoginGroupId: any;
};

const AuthStateContext = React.createContext<State | undefined>(undefined);

const AuthDispatchContext = React.createContext<Dispatch | undefined>(undefined);

const defaultState = {
  isAuthenticated: false,
  accessToken: '',
  refreshToken: '',
  groupLoginGroupId: null,
};

async function getInitialState() {
  try {
    const accessToken = await AsyncStorage.get('accessToken');
    const refreshToken = await AsyncStorage.get('refreshToken');
    const groupLoginGroupId = await AsyncStorage.get('groupLoginGroupId');
    configureBootScreen(!!refreshToken);
    return {
      accessToken,
      refreshToken,
      isAuthenticated: !!refreshToken,
      groupLoginGroupId,
    } as State;
  } catch (error) {
    return defaultState;
  }
}

export const authReducer = (state: any, action: any) => {
  switch (action.type) {
    case 'LOGIN':
      trackingLogin();
      return {
        ...state,
        isAuthenticated: true,
        accessToken: action.payload.access_token,
        refreshToken: action.payload.refresh_token,
      };
    case 'LOGOUT':
      trackingLogout();
      return executeLogout(false).then(() => {
        return defaultState;
      });

    case 'GROUP_LOGIN_SET_ID':
      return {
        ...state,
        groupLoginGroupId: action.payload.groupLoginGroupId,
      };

    case 'GROUP_LOGIN_UNSET_ID':
      removeGroupLoginGroupId();
      return {
        ...state,
        groupLoginGroupId: null,
      };

    default:
      return state;
  }
};

export const AuthProvider: React.ComponentType<React.ReactNode> = ({ children }: any) => {
  const [state, setState] = useState<any>(null);

  useEffect(() => {
    getInitialState().then((response) => {
      setState(response);
    });
  }, []);

  if (!state) return <BootScreen />;

  return <Provider initialState={state}>{children}</Provider>;
};

const Provider = ({ children, initialState }: any) => {
  const [state, dispatch] = React.useReducer(authReducer, initialState);
  return (
    <AuthStateContext.Provider value={state}>
      <AuthDispatchContext.Provider value={dispatch}>{children}</AuthDispatchContext.Provider>
    </AuthStateContext.Provider>
  );
};

export const useAuthState = () => {
  const context = React.useContext(AuthStateContext);
  if (context === undefined) {
    throw new Error('useAuthState must be used within a AuthProvider');
  }
  return context;
};

export const useAuthDispatch = () => {
  const context = React.useContext(AuthDispatchContext);
  if (context === undefined) {
    throw new Error('useAuthDispatch must be used within a AuthProvider');
  }
  return context;
};
